<template>
  <!-- 다운로드 앱 편집 모달 -->
  <a-modal
    :visible="visible"
    :footer="null"
    :mask-closable="false"
    :width="520"
    @cancel="handleCancel"
  >
    <template #title>
      <div class="modal-title">
        {{ isNew ? '다운로드 추가' : '다운로드 편집' }}
      </div>
    </template>

    <div class="modal-content">
      <!-- 앱 이름 (필수) -->
      <div class="field">
        <label class="field__label">앱 이름 <span class="required">*</span></label>
        <a-input
          v-model:value="form.appName"
          placeholder="앱 이름"
        />
      </div>

      <!-- 플랫폼 -->
      <div class="field">
        <label class="field__label">플랫폼</label>
        <a-select
          v-model:value="form.platform"
          style="width: 100%;"
          placeholder="플랫폼 선택"
        >
          <a-select-option value="iOS">iOS</a-select-option>
          <a-select-option value="Android">Android</a-select-option>
          <a-select-option value="Windows">Windows</a-select-option>
          <a-select-option value="macOS">macOS</a-select-option>
          <a-select-option value="Web">Web</a-select-option>
        </a-select>
      </div>

      <!-- 버전 -->
      <div class="field">
        <label class="field__label">버전</label>
        <a-input
          v-model:value="form.version"
          placeholder="예: 1.2.0"
        />
      </div>

      <!-- 다운로드 URL (필수) -->
      <div class="field">
        <label class="field__label">다운로드 URL <span class="required">*</span></label>
        <a-input
          v-model:value="form.downloadUrl"
          type="url"
          placeholder="https://..."
        />
      </div>

      <!-- 앱 아이콘 이미지 -->
      <div class="field">
        <label class="field__label">앱 아이콘</label>
        <image-uploader
          :model-value="form.iconSrc"
          :max-size-m-b="2"
          @update:model-value="(url) => (form.iconSrc = url)"
        />
      </div>

      <!-- 설명 -->
      <div class="field">
        <label class="field__label">설명</label>
        <a-textarea
          v-model:value="form.description"
          :rows="3"
          placeholder="앱 간단 설명"
        />
      </div>

      <!-- 활성 여부 -->
      <div class="field field--switch">
        <label class="field__label">활성화</label>
        <a-switch v-model:checked="form.isActive" />
      </div>

      <!-- 카테고리 표시 (읽기 전용) -->
      <div class="field">
        <label class="field__label">카테고리</label>
        <a-tag :color="category === 'hospital' ? 'blue' : 'green'">
          {{ category === 'hospital' ? '병원용' : '고객용' }}
        </a-tag>
      </div>

      <!-- 모달 푸터 -->
      <div class="modal-footer">
        <!-- 기존 항목 삭제 버튼 -->
        <a-popconfirm
          v-if="!isNew"
          title="이 항목을 삭제하시겠습니까?"
          ok-text="삭제"
          cancel-text="취소"
          ok-type="danger"
          @confirm="handleDelete"
        >
          <a-button type="text" danger :loading="loading">삭제</a-button>
        </a-popconfirm>

        <div style="flex: 1;" />

        <a-button @click="handleCancel" :disabled="loading">취소</a-button>
        <a-button
          type="primary"
          class="yellow-button"
          :loading="loading"
          @click="handleSave"
        >
          저장
        </a-button>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { message } from 'ant-design-vue';
import { mapActions, mapMutations } from 'vuex';
import ImageUploader from '../common/ImageUploader.vue';
import { HomepageApi } from '../../api';

export default {
  name: 'DownloadAppEditor',

  components: {
    ImageUploader,
  },

  props: {
    // 모달 표시 여부
    visible: {
      type: Boolean,
      default: false,
    },
    // 편집할 앱 (null이면 신규)
    app: {
      type: Object,
      default: null,
    },
    // 앱 인덱스
    appIndex: {
      type: Number,
      default: -1,
    },
    // 카테고리: 'hospital' | 'customer'
    category: {
      type: String,
      default: 'hospital',
    },
  },

  emits: ['cancel'],

  data() {
    return {
      loading: false,
      // 로컬 편집 폼 데이터
      form: {
        appName: '',
        platform: undefined,
        version: '',
        downloadUrl: '',
        iconSrc: '',
        description: '',
        isActive: true,
      },
    };
  },

  computed: {
    // 신규 항목 여부
    isNew() {
      return !this.app;
    },
  },

  watch: {
    // 모달 열릴 때 폼 데이터 초기화
    visible(val) {
      if (val) {
        this.initForm();
      }
    },
  },

  methods: {
    ...mapActions('homepageEditor', ['loadPageData']),
    ...mapMutations('homepageEditor', ['SET_PAGE_DATA']),

    // 폼 데이터 초기화
    initForm() {
      if (this.app) {
        this.form = {
          appName: this.app.appName || '',
          platform: this.app.platform || undefined,
          version: this.app.version || '',
          downloadUrl: this.app.downloadUrl || '',
          iconSrc: this.app.iconSrc || '',
          description: this.app.description || '',
          isActive: this.app.isActive !== false,
        };
      } else {
        this.form = {
          appName: '',
          platform: undefined,
          version: '',
          downloadUrl: '',
          iconSrc: '',
          description: '',
          isActive: true,
        };
      }
    },

    // 입력 유효성 검사
    validate() {
      if (!this.form.appName || !this.form.appName.trim()) {
        message.warning('앱 이름을 입력해주세요.');
        return false;
      }
      if (!this.form.downloadUrl || !this.form.downloadUrl.trim()) {
        message.warning('다운로드 URL을 입력해주세요.');
        return false;
      }
      return true;
    },

    // downloads 데이터 강제 리로드
    async forceReloadDownloads() {
      this.SET_PAGE_DATA({ page: 'downloads', data: null });
      await this.loadPageData('downloads');
    },

    // 저장 처리 (신규/수정)
    async handleSave() {
      if (!this.validate()) return;

      this.loading = true;
      try {
        // 카테고리를 payload에 포함
        const payload = {
          ...this.form,
          category: this.category,
        };

        if (this.isNew) {
          await HomepageApi.createDownloadApp(payload);
          message.success('다운로드 항목이 추가되었습니다.');
        } else {
          const id = this.app._id || this.app.id;
          await HomepageApi.updateDownloadApp(id, payload);
          message.success('다운로드 항목이 수정되었습니다.');
        }

        await this.forceReloadDownloads();
        this.$emit('cancel');
      } catch (error) {
        message.error(error.message || '저장 중 오류가 발생했습니다.');
      } finally {
        this.loading = false;
      }
    },

    // 삭제 처리
    async handleDelete() {
      if (!this.app) return;

      this.loading = true;
      try {
        const id = this.app._id || this.app.id;
        await HomepageApi.deleteDownloadApp(id);
        message.success('다운로드 항목이 삭제되었습니다.');

        await this.forceReloadDownloads();
        this.$emit('cancel');
      } catch (error) {
        message.error(error.message || '삭제 중 오류가 발생했습니다.');
      } finally {
        this.loading = false;
      }
    },

    // 취소/닫기
    handleCancel() {
      if (!this.loading) {
        this.$emit('cancel');
      }
    },
  },
};
</script>

<style scoped>
.field {
  margin-bottom: 16px;
}

.field__label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #333;
}

.field--switch {
  display: flex;
  align-items: center;
  gap: 12px;
}

.field--switch .field__label {
  margin-bottom: 0;
}

.required {
  color: #ff4d4f;
}

.modal-footer {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}
</style>
